// Dashboard.jsx — Content Archives
// Design: Consistent with App.jsx — same tokens, same typography, same spacing

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import {
  Container,
  Typography,
  TextField,
  IconButton,
  Pagination,
  Stack,
  Tooltip,
  InputAdornment,
  Chip,
  Box,
  Skeleton,
  ThemeProvider,
  createTheme,
  CssBaseline,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";

// ─── Theme ────────────────────────────────────────────────────────────────────
const muiTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#6c7ef8" },
    secondary: { main: "#34d399" },
    background: { default: "#111318", paper: "#191c24" },
    text: { primary: "#f0f2f7", secondary: "#b0b7ce" },
  },
  typography: { fontFamily: "'DM Sans', system-ui, sans-serif" },
});

// ✅ Shared design tokens — kept in sync with App.jsx and AccountPage.jsx
const D = {
  bg: "#111318",
  surface: "#191c24",
  surfaceUp: "#1f2230",
  surfaceHov: "#1e2131",
  border: "rgba(255,255,255,0.07)",
  borderMed: "rgba(255,255,255,0.11)",
  accent: "#6c7ef8",
  accentSoft: "rgba(108,126,248,0.1)",
  accentBorder: "rgba(108,126,248,0.2)",
  success: "#34d399",
  successSoft: "rgba(52,211,153,0.1)",
  warn: "#f59e0b",
  warnSoft: "rgba(245,158,11,0.1)",
  danger: "#f87171",
  dangerSoft: "rgba(248,113,113,0.1)",
  // ✅ Fixed text colors for readability
  textPrimary: "#f0f2f7",
  textSec: "#b0b7ce", // was #8b91a8
  textMuted: "#6b7390", // was #555b72
  fontDisplay: "'Plus Jakarta Sans', 'DM Sans', sans-serif",
  fontBody: "'DM Sans', sans-serif",
  fontMono: "'JetBrains Mono', monospace",
};

// ─── Global styles ────────────────────────────────────────────────────────────
const G = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=DM+Sans:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; }
  body { background: #111318; color: #f0f2f7; font-family: 'DM Sans', sans-serif; -webkit-font-smoothing: antialiased; }
  ::selection { background: rgba(108,126,248,0.25); }
  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius:99px; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.17); }

  @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin { to{transform:rotate(360deg)} }
  @keyframes shimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.35} }
  // Add inside the G template string, after the existing @keyframes
@keyframes logoGlow { 0%,100% { box-shadow: 0 0 16px rgba(108,126,248,0.3); } 50% { box-shadow: 0 0 28px rgba(163,123,248,0.45); } }

  .fade-up   { animation: fadeUp 0.45s cubic-bezier(0.22,1,0.36,1) both; }
  .fade-up-1 { animation-delay:0.07s; }
  .fade-up-2 { animation-delay:0.14s; }
  .fade-up-3 { animation-delay:0.21s; }

  .page-glow {
    position:fixed; inset:0; z-index:0; pointer-events:none;
    background:
      radial-gradient(ellipse 60% 40% at 10% 0%, rgba(108,126,248,0.04) 0%, transparent 65%),
      radial-gradient(ellipse 40% 35% at 90% 100%, rgba(52,211,153,0.03) 0%, transparent 65%);
  }

  /* MUI overrides */
  .MuiOutlinedInput-root { border-radius:10px !important; background:rgba(255,255,255,0.03) !important; font-family:'DM Sans',sans-serif !important; font-size:14px !important; }
  .MuiOutlinedInput-notchedOutline { border-color:rgba(255,255,255,0.09) !important; transition:border-color 0.2s !important; }
  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline { border-color:rgba(255,255,255,0.18) !important; }
  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline { border-color:#6c7ef8 !important; border-width:1.5px !important; }
  .MuiOutlinedInput-root.Mui-focused { box-shadow:0 0 0 3px rgba(108,126,248,0.12) !important; }
  .MuiInputLabel-root { font-family:'DM Sans',sans-serif !important; color:#8b91a8 !important; }
  .MuiInputLabel-root.Mui-focused { color:#6c7ef8 !important; }
  .MuiPaginationItem-root { font-family:'DM Sans',sans-serif !important; font-size:12.5px !important; color:#b0b7ce !important; border-color:rgba(255,255,255,0.09) !important; border-radius:8px !important; background:#191c24 !important; }
  .MuiPaginationItem-root:hover { background:#1f2230 !important; border-color:rgba(255,255,255,0.18) !important; }
  .MuiPaginationItem-root.Mui-selected { background:#6c7ef8 !important; color:white !important; border-color:#6c7ef8 !important; }
  .MuiTooltip-tooltip { background:#1f2230 !important; border:1px solid rgba(255,255,255,0.1) !important; border-radius:8px !important; font-family:'DM Sans',sans-serif !important; font-size:12px !important; color:#f0f2f7 !important; padding:6px 10px !important; max-width:280px !important; word-break:break-all !important; }
  .MuiTooltip-arrow { color:#1f2230 !important; }

  /* Row hover */
  .file-row { transition: background 0.15s; }
  .file-row:hover { background: #1e2131 !important; }
  .file-row .row-actions { opacity: 0.4; transition: opacity 0.15s; }
  .file-row:hover .row-actions { opacity: 1; }

  /* Spinner */
  .spin { display:inline-block; width:14px; height:14px; border-radius:50%; border:2px solid rgba(108,126,248,0.25); border-top-color:#6c7ef8; animation:spin 0.7s linear infinite; flex-shrink:0; }
  .spin-danger { border-color:rgba(248,113,113,0.25) !important; border-top-color:#f87171 !important; }

  /* Download overlay */
  .dl-overlay {
    position:fixed; inset:0; z-index:9999;
    background:rgba(17,19,24,0.82); backdrop-filter:blur(8px);
    display:flex; flex-direction:column; align-items:center; justify-content:center; gap:16px;
  }
  .progress-shimmer {
    height:2px; border-radius:99px; width:140px; overflow:hidden;
    background:rgba(255,255,255,0.06);
  }
  .progress-shimmer::after {
    content:''; display:block; height:100%;
    background:linear-gradient(90deg,transparent,#6c7ef8,transparent);
    background-size:200% 100%;
    animation:shimmer 1.4s ease infinite;
  }
  @media (max-width: 768px) {
    .file-row .row-actions { opacity: 1 !important; }
    .file-row { flex-wrap: wrap; }
  }
  @media (max-width: 600px) {
    .MuiPaginationItem-root { min-width: 28px !important; height: 28px !important; font-size: 11px !important; }
  }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getBaseName = (p = "") => p.split("/").pop().split("\\").pop();

  
const truncateName = (name, max = 42) => {
  if (name.length <= max) return { display: name, truncated: false };
  const ext = name.includes(".") ? "." + name.split(".").pop() : "";
  const stem = name.slice(0, name.length - ext.length);
  const keep = max - ext.length - 1;
  return {
    display:
      stem.slice(0, Math.ceil(keep * 0.6)) +
      "…" +
      stem.slice(-Math.floor(keep * 0.35)) +
      ext,
    truncated: true,
  };
};

const humanSize = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const platformStripe = (name) => {
  const n = name.toLowerCase();
  if (n.includes("instagram") || n.includes("ig_")) return "#c13584";
  if (n.includes("facebook") || n.includes("fb_")) return "#3b82f6";
  if (n.includes("linkedin") || n.includes("li_")) return "#0a66c2";
  if (n.includes("twitter") || n.includes("tw_") || n.includes("x_"))
    return "#38bdf8";
  if (n.includes("newsletter") || n.includes("email")) return D.success;
  if (n.includes("blog")) return D.warn;
  return D.accent;
};

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, accent = D.accent, loading }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        background: D.surface,
        border: `1px solid ${D.border}`,
        borderRadius: "12px",
        p: "18px 22px",
        display: "flex",
        alignItems: "center",
        gap: 2,
        transition: "border-color 0.2s, transform 0.2s",
        "&:hover": {
          borderColor: `${accent}44`,
          transform: "translateY(-1px)",
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          background: `${accent}14`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          sx={{
            fontFamily: D.fontMono,
            fontSize: "10.5px",
            color: D.textMuted,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            mb: 0.5,
          }}
        >
          {label}
        </Typography>
        {loading ? (
          <Box
            sx={{
              width: 60,
              height: 20,
              borderRadius: "6px",
              background: "rgba(255,255,255,0.06)",
              backgroundImage:
                "linear-gradient(90deg, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.08) 300px, rgba(255,255,255,0.04) 600px)",
              backgroundSize: "600px",
              animation: "shimmer 1.6s ease infinite",
            }}
          />
        ) : (
          <Typography
            sx={{
              fontFamily: D.fontDisplay,
              fontWeight: 700,
              fontSize: "20px",
              color: D.textPrimary,
              lineHeight: 1,
            }}
          >
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── Skeleton row ─────────────────────────────────────────────────────────────
function SkeletonRow() {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "36px 1fr 100px 200px 88px",
        gap: "0 16px",
        alignItems: "center",
        px: 3,
        py: 2.5,
        borderBottom: `1px solid ${D.border}`,
      }}
    >
      {[36, 240, 70, 130, 60].map((w, i) => (
        <Box
          key={i}
          sx={{
            height: 13,
            width: w,
            borderRadius: "5px",
            background: "rgba(255,255,255,0.05)",
            backgroundImage:
              "linear-gradient(90deg,rgba(255,255,255,0.04) 0px,rgba(255,255,255,0.08) 300px,rgba(255,255,255,0.04) 600px)",
            backgroundSize: "600px",
            animation: "shimmer 1.6s ease infinite",
          }}
        />
      ))}
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function Dashboard() {
  const [files, setFiles] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [totalFiles, setTotalFiles] = useState(0);
  const [totalSize, setTotalSize] = useState(0);
  const [latestDate, setLatestDate] = useState(null);
  const pageSize = 10;

  const navigate = useNavigate();

  const email = (() => {
    try {
      const token = localStorage.getItem("auth_token");
      return JSON.parse(atob(token.split(".")[1])).sub || "";
    } catch { return ""; }
  })();

  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    navigate("/login");
  };


  const fetchFiles = async () => {
    const userId = localStorage.getItem("email");
    setLoading(true);
    try {
      const res = await api.get("/dashboard/fetch_files", {
        params: { search, page, page_size: pageSize },
      });
      const data = res.data;
      const items = data.items || [];
      setFiles(items);
      setTotalPages(data.total_pages || 1);
      setTotalFiles(data.total_count || items.length);
      setTotalSize(
        data.total_size || items.reduce((s, f) => s + (f.size || 0), 0),
      );
      if (items.length > 0) {
        const latest = items.reduce((a, b) =>
          new Date(a.updated) > new Date(b.updated) ? a : b,
        );
        setLatestDate(latest.updated);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [search, page]);

  const handleDownload = async (fileName) => {
    setDownloading(fileName);
    try {
      const res = await api.get("/dashboard/files_download_req", {
        params: { file_name: fileName },
        responseType: "blob",
      });
      const blob = res.data; // axios puts blob directly here
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = getBaseName(fileName);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (fileName) => {
    setDeleting(fileName);
    try {
       await api.delete("/dashboard/selected_files", { data: { file_name: fileName } });
    await fetchFiles();
    } catch (e) {
      console.error(e);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <style>{G}</style>
      {/* ── Sticky Nav ── */}
<div style={{
  position: "sticky", top: 0, zIndex: 200,
  background: "rgba(17,19,24,0.88)", backdropFilter: "blur(16px)",
  borderBottom: `1px solid ${D.border}`,
}}>
  <div style={{
    maxWidth: 1160, margin: "0 auto", padding: "0 24px",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    height: 60,
  }}>
    {/* Logo + page label */}
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => navigate("/home")}>
        <div style={{ width: 28, height: 28, borderRadius: 8, background: `linear-gradient(135deg, ${D.accent} 0%, #a37bf8 100%)`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: D.fontDisplay, fontWeight: 800, fontSize: 14, color: "#fff", boxShadow: "0 4px 14px rgba(108,126,248,0.35)" }}>Z</div>
        <span style={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 15, color: D.textPrimary, letterSpacing: "-0.02em" }}>
          zenithpost<span style={{ color: D.accent }}>.ai</span>
        </span>
      </div>
      <div style={{ width: 1, height: 18, background: D.border }} />
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.22)",
        borderRadius: 99, padding: "3px 10px",
      }}>
        <div style={{ width: 5, height: 5, borderRadius: "50%", background: D.warn }} />
        <span style={{ fontFamily: D.fontMono, fontSize: 10, fontWeight: 700, color: D.warn, textTransform: "uppercase", letterSpacing: "0.1em" }}>
          Content Archive
        </span>
      </div>
    </div>

    {/* Right side */}
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <button
        onClick={() => navigate("/home")}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "6px 13px", borderRadius: 8,
          background: "rgba(255,255,255,0.04)", border: `1px solid ${D.border}`,
          color: D.textSec, fontFamily: D.fontBody, fontSize: 13,
          fontWeight: 600, cursor: "pointer",
        }}
      >
        ← Back to Home
      </button>
      <button
        onClick={() => navigate("/account")}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "6px 13px", borderRadius: 8,
          background: "rgba(255,255,255,0.04)", border: `1px solid ${D.border}`,
          color: D.textSec, fontFamily: D.fontBody, fontSize: 13,
          fontWeight: 600, cursor: "pointer",
        }}
      >
        Account
      </button>
      <div style={{
        fontFamily: D.fontMono, fontSize: 11,
        color: D.textMuted, maxWidth: 180,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        {email}
      </div>
      <button
        onClick={handleLogout}
        style={{
          padding: "6px 14px", borderRadius: 8,
          border: "1px solid rgba(248,113,113,0.25)",
          background: "rgba(248,113,113,0.07)",
          color: D.danger, fontFamily: D.fontBody,
          fontSize: 13, fontWeight: 600, cursor: "pointer",
        }}
      >
        Log out
      </button>
    </div>
  </div>
</div>
      <div className="page-glow" />

      {/* Download overlay */}
      {downloading && (
        <div className="dl-overlay">
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "14px",
              background: D.accentSoft,
              border: `1px solid ${D.accentBorder}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CloudDownloadOutlinedIcon
              sx={{ color: D.accent, fontSize: "26px !important" }}
            />
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              sx={{
                fontFamily: D.fontDisplay,
                fontWeight: 700,
                fontSize: "15px",
                color: D.textPrimary,
                mb: 0.5,
              }}
            >
              Preparing download…
            </Typography>
            <Typography
              sx={{
                fontFamily: D.fontMono,
                fontSize: "11.5px",
                color: D.textMuted,
                maxWidth: 280,
              }}
            >
              {truncateName(getBaseName(downloading), 36).display}
            </Typography>
          </Box>
          <div className="progress-shimmer" />
        </div>
      )}

      <Container
        maxWidth="lg"
        sx={{ pt: 5, pb: 8, position: "relative", zIndex: 1 }}
      >
        {/* ── Header ── */}
        <Box className="fade-up" sx={{ mb: 5 }}>
          <Typography
            sx={{
              fontFamily: D.fontMono,
              fontSize: "11px",
              color: D.textMuted,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              mb: 1.25,
            }}
          >
            Content Archive
          </Typography>
          <Typography
            sx={{
              fontFamily: D.fontDisplay,
              fontWeight: 800,
              fontSize: { xs: "24px", md: "32px" },
              color: D.textPrimary,
              lineHeight: 1.15,
              mb: 1.25,
            }}
          >
            Generated Content Files
          </Typography>
          {/* ✅ Fixed: description text is now readable */}
          <Typography
            sx={{
              fontSize: "14.5px",
              color: D.textSec,
              maxWidth: 460,
              lineHeight: 1.7,
            }}
          >
            Download or manage your content packages. Each ZIP contains
            platform-ready posts, captions, and assets.
          </Typography>
        </Box>

        {/* ── Stat cards ── */}
        <Box
          className="fade-up fade-up-1"
          sx={{ display: "flex", gap: 2, mb: 4, flexWrap: "wrap" }}
        >
          <StatCard
            icon={
              <FolderZipOutlinedIcon
                sx={{ fontSize: "20px !important", color: D.warn }}
              />
            }
            label="Total Files"
            value={totalFiles}
            accent={D.warn}
            loading={loading}
          />
          <StatCard
            icon={
              <StorageOutlinedIcon
                sx={{ fontSize: "20px !important", color: D.accent }}
              />
            }
            label="Total Size"
            value={humanSize(totalSize)}
            accent={D.accent}
            loading={loading}
          />
          <StatCard
            icon={
              <CalendarTodayOutlinedIcon
                sx={{ fontSize: "20px !important", color: D.success }}
              />
            }
            label="Latest Update"
            value={
              latestDate
                ? new Date(latestDate).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })
                : "—"
            }
            accent={D.success}
            loading={loading}
          />
        </Box>

        {/* ── Search ── */}
        <Box className="fade-up fade-up-2" sx={{ mb: 3 }}>
          <TextField
            placeholder="Search by file name, platform, or topic…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{ fontSize: "18px !important", color: D.textMuted }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{ width: "100%", maxWidth: 440 }}
          />
        </Box>

        {/* ── Table ── */}
        <Box
          className="fade-up fade-up-3"
          sx={{
            background: D.surface,
            border: `1px solid ${D.border}`,
            borderRadius: "14px",
            overflow: "hidden",
          }}
        >
          {/* Column headers */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "36px 1fr 100px 200px 88px",
              gap: "0 16px",
              alignItems: "center",
              px: 3,
              py: 2,
              borderBottom: `1px solid ${D.border}`,
              background: "rgba(255,255,255,0.02)",
            }}
          >
            {["", "File Name", "Size", "Last Updated", "Actions"].map(
              (col, i) => (
                <Typography
                  key={i}
                  sx={{
                    fontFamily: D.fontMono,
                    fontSize: "10.5px",
                    fontWeight: 600,
                    color: D.textMuted,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    textAlign: i === 4 ? "right" : "left",
                  }}
                >
                  {col}
                </Typography>
              ),
            )}
          </Box>

          {/* Rows */}
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
          ) : files.length === 0 ? (
            <Box
              sx={{
                py: 10,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <FolderZipOutlinedIcon
                sx={{ fontSize: "40px !important", color: D.textMuted }}
              />
              {/* ✅ Fixed: empty state text is readable */}
              <Typography sx={{ fontSize: "14px", color: D.textSec }}>
                {search
                  ? `No files matching "${search}"`
                  : "No archives yet — generate some content to get started."}
              </Typography>
            </Box>
          ) : (
            files.map((file, idx) => {
              const base = getBaseName(file.name);
              const { display, truncated } = truncateName(base, 42);
              const stripe = platformStripe(base);
              const isDl = downloading === file.name;
              const isDel = deleting === file.name;

              return (
                <Box
                  key={file.name}
                  className="file-row"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "36px 1fr 100px 200px 88px",
                    gap: "0 16px",
                    alignItems: "center",
                    px: 3,
                    py: 2.5,
                    borderBottom:
                      idx < files.length - 1 ? `1px solid ${D.border}` : "none",
                    animation: "fadeUp 0.3s ease both",
                    animationDelay: `${idx * 0.035}s`,
                  }}
                >
                  {/* File icon */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FolderZipOutlinedIcon
                      sx={{
                        fontSize: "18px !important",
                        color: D.warn,
                        opacity: 0.75,
                      }}
                    />
                  </Box>

                  {/* File name */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 3,
                        height: 32,
                        borderRadius: 99,
                        background: stripe,
                        opacity: 0.7,
                        flexShrink: 0,
                      }}
                    />
                    {truncated ? (
                      <Tooltip title={base} placement="top" arrow>
                        <Typography
                          sx={{
                            fontFamily: D.fontMono,
                            fontSize: "12.5px",
                            fontWeight: 500,
                            color: D.textPrimary,
                            cursor: "default",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {display}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography
                        sx={{
                          fontFamily: D.fontMono,
                          fontSize: "12.5px",
                          fontWeight: 500,
                          color: D.textPrimary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {display}
                      </Typography>
                    )}
                  </Box>

                  {/* ✅ Fixed: size and date text is readable */}
                  <Typography
                    sx={{
                      fontFamily: D.fontMono,
                      fontSize: "12px",
                      color: D.textSec,
                    }}
                  >
                    {humanSize(file.size)}
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: D.fontMono,
                      fontSize: "12px",
                      color: D.textSec,
                    }}
                  >
                    {formatDate(file.updated)}
                  </Typography>

                  {/* Actions */}
                  <Box
                    className="row-actions"
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 0.5,
                    }}
                  >
                    <Tooltip title="Download" arrow>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!!isDl}
                          onClick={() => handleDownload(file.name)}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "8px",
                            color: D.textSec,
                            border: "1px solid transparent",
                            transition: "all 0.15s",
                            "&:hover": {
                              background: D.accentSoft,
                              borderColor: D.accentBorder,
                              color: D.accent,
                            },
                          }}
                        >
                          {isDl ? (
                            <span className="spin" />
                          ) : (
                            <DownloadIcon
                              sx={{ fontSize: "15px !important" }}
                            />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!!isDel}
                          onClick={() => handleDelete(file.name)}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "8px",
                            color: D.textSec,
                            border: "1px solid transparent",
                            transition: "all 0.15s",
                            "&:hover": {
                              background: D.dangerSoft,
                              borderColor: "rgba(248,113,113,0.25)",
                              color: D.danger,
                            },
                          }}
                        >
                          {isDel ? (
                            <span className="spin spin-danger" />
                          ) : (
                            <DeleteIcon sx={{ fontSize: "15px !important" }} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <Stack alignItems="center" sx={{ mt: 3 }}>
            <Pagination
              page={page}
              count={totalPages}
              variant="outlined"
              shape="rounded"
              onChange={(_, v) => setPage(v)}
              sx={{ "& .MuiPagination-ul": { gap: 0.5 } }}
            />
          </Stack>
        )}

        {/* ── Hint ── */}
        <Typography
          sx={{
            fontFamily: D.fontMono,
            fontSize: "11px",
            color: D.textMuted,
            textAlign: "center",
            mt: 4,
          }}
        >
          Files are stored per account · Hover a truncated filename to see its
          full path
        </Typography>

        <Box sx={{ height: 40 }} />
      </Container>
    </ThemeProvider>
  );
}
